/**
 * @file `CoursesPanel` — root component of the `/courses` route
 *       (umbrella #883, part #892).
 * @module @labcraft/admin/ui/courses/courses_panel
 *
 * Operator surface for lab's `installed_courses` table, rebuilt on the
 * shared panel chrome: a left rail (All / Enabled / Disabled), a table
 * with status pills + an enable switch + Uninstall, the **Install
 * modal** (replaces the prod `window.prompt` chain), and a row-detail
 * `LowerSplit` lower pane. Enrollment / chapter data has no admin
 * source yet, so those read an honest `—` / "not connected".
 *
 * All data flows through the TanStack hook layer at
 * `../hooks/use_courses.ts` — components never call `useQuery` /
 * `useMutation` directly.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react'
import {
  useCoursesList,
  useInstallCourseMutation,
  usePagedList,
  useUninstallCourseMutation,
  useUpdateCourseMutation,
} from '../hooks/index.ts'
import type { InstalledCourse } from '../../shared/courses_routes.ts'
import { LowerSplit } from '../kernel/lower_split.tsx'
import { Pager } from '../kernel/pager.tsx'
import { CourseView, type CourseFilter } from './view.ts'

/** Render the Courses route. */
export function CoursesPanel(): JSX.Element {
  const list = useCoursesList()
  const install = useInstallCourseMutation()
  const update = useUpdateCourseMutation()
  const uninstall = useUninstallCourseMutation()

  const [filter, setFilter] = useState<CourseFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [installOpen, setInstallOpen] = useState(false)

  const all = useMemo(() => list.data ?? [], [list.data])
  const rows = useMemo(() => CourseView.filter(all, filter), [all, filter])
  const counts = useMemo(() => CourseView.counts(all), [all])
  const selected = all.find(c => c.courseId === selectedId) ?? null

  const scrollRef = useRef<HTMLDivElement>(null)
  const { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount } =
    usePagedList(scrollRef, rows.length)
  // A rail filter change starts a fresh page.
  useEffect(() => {
    resetPage()
  }, [filter, resetPage])
  const paged = rows.slice(offset, offset + pageSize)

  const listError = list.isError
    ? list.error.message || 'Could not load courses.'
    : null
  const mutationError =
    install.error?.message ??
    update.error?.message ??
    uninstall.error?.message ??
    null
  const pending = install.isPending || update.isPending || uninstall.isPending

  const onToggle = useCallback(
    (courseId: string, enabled: boolean) => {
      update.mutate({ courseId, patch: { enabled } })
    },
    [update],
  )

  const onUninstall = useCallback(
    (courseId: string) => {
      if (
        !window.confirm(
          `Uninstall "${courseId}"? Artifact files on disk are NOT deleted.`,
        )
      )
        return
      uninstall.mutate(courseId, { onSuccess: () => setSelectedId(null) })
    },
    [uninstall],
  )

  const RAIL: ReadonlyArray<{
    id: CourseFilter
    label: string
    count: number
  }> = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'enabled', label: 'Enabled', count: counts.enabled },
    { id: 'disabled', label: 'Disabled', count: counts.disabled },
  ]

  return (
    <div className="panel-split">
      <nav className="panel-rail" aria-label="Course filters">
        <div className="panel-rail__h">Catalog</div>
        {RAIL.map(r => (
          <button
            key={r.id}
            className="panel-rail__item"
            aria-current={filter === r.id ? 'page' : undefined}
            onClick={() => setFilter(r.id)}>
            <span>{r.label}</span>
            <span className="panel-rail__count">{r.count}</span>
          </button>
        ))}
        <div className="panel-rail__foot">
          // rows live in the shared installed_courses table
        </div>
      </nav>

      <div className="panel panel--no-subbar">
        <header className="panel-head">
          <div>
            <div className="panel-head__title-row">
              <h1 className="panel-head__title">Courses</h1>
              <span className="panel-head__count">
                {list.data ? `${all.length} installed` : '—'}
              </span>
            </div>
            <p className="panel-head__sub">// operator-managed catalog</p>
          </div>
          <button
            type="button"
            className="lc-btn lc-btn--primary panel-head__action"
            disabled={pending}
            onClick={() => setInstallOpen(true)}>
            Install course
          </button>
        </header>

        {(listError !== null || mutationError !== null) && (
          <div className="panel-banner panel-banner--error">
            <b>
              {listError !== null
                ? 'Could not load courses.'
                : 'Action failed.'}
            </b>{' '}
            {listError ?? mutationError}
          </div>
        )}

        <LowerSplit
          open={selected !== null}
          storageKey="lc-courses-split"
          detail={
            selected && (
              <CourseDetail
                course={selected}
                onClose={() => setSelectedId(null)}
              />
            )
          }>
          <div className="paged-list">
            <div
              className="table-scroll"
              ref={scrollRef}
              style={
                rowHeight > 0
                  ? ({ '--row-h': `${rowHeight}px` } as CSSProperties)
                  : undefined
              }>
              <table className="table">
                <thead>
                  <tr>
                    <th>course</th>
                    <th>version</th>
                    <th>enrolled</th>
                    <th>installed</th>
                    <th>status</th>
                    <th>enabled</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="table-empty">
                        {list.isPending
                          ? '// loading…'
                          : '// no courses. Click "Install course" to add one.'}
                      </td>
                    </tr>
                  ) : (
                    paged.map(row => {
                      const status = CourseView.status(row)
                      return (
                        <tr
                          key={row.courseId}
                          aria-selected={row.courseId === selectedId}
                          onClick={() => setSelectedId(row.courseId)}>
                          <td className="cell-code">{row.courseId}</td>
                          <td className="cell-meta">{row.version}</td>
                          <td className="cell-empty">—</td>
                          <td className="cell-meta">
                            {row.installedAt.slice(0, 10)}
                          </td>
                          <td>
                            <span
                              className={`spill ${status === 'enabled' ? 'spill--ok' : 'spill--idle'}`}>
                              {status}
                            </span>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <label className="lc-switch">
                              <input
                                type="checkbox"
                                checked={row.enabledAt !== null}
                                disabled={pending}
                                onChange={e =>
                                  onToggle(row.courseId, e.target.checked)
                                }
                              />
                              <span className="lc-switch__thumb" aria-hidden />
                            </label>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <button
                              type="button"
                              className="lc-btn lc-btn--danger"
                              disabled={pending}
                              onClick={() => onUninstall(row.courseId)}>
                              Uninstall
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="paged-foot">
              <Pager
                page={page}
                pageCount={pageCount}
                onPage={setPage}
                total={list.data ? rows.length : undefined}
              />
            </div>
          </div>
        </LowerSplit>
      </div>

      {installOpen && (
        <InstallModal
          pending={install.isPending}
          onClose={() => setInstallOpen(false)}
          onSubmit={args =>
            install.mutate(args, { onSuccess: () => setInstallOpen(false) })
          }
        />
      )}
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Row detail (lower pane)
////////////////////////////////////////////////////////////////////////////////

/** Installed-course detail rendered in the LowerSplit lower pane. */
function CourseDetail(props: {
  course: InstalledCourse
  onClose: () => void
}): JSX.Element {
  const { course, onClose } = props
  return (
    <div>
      <div className="detail-head">
        <span className="detail-head__crumb">
          Courses › <b>{course.courseId}</b>
        </span>
        <button
          className="detail-head__close"
          onClick={onClose}
          aria-label="Close detail">
          ✕
        </button>
      </div>

      <section
        className="detail-section"
        style={{ marginTop: 0, borderTop: 0, paddingTop: 0 }}>
        <h3 className="detail-section__h">Registry</h3>
        <dl className="kv-grid">
          <dt>course id</dt>
          <dd>{course.courseId}</dd>
          <dt>version</dt>
          <dd>{course.version}</dd>
          <dt>source path</dt>
          <dd>{course.sourcePath}</dd>
          <dt>installed</dt>
          <dd>{course.installedAt.slice(0, 10)}</dd>
          <dt>status</dt>
          <dd>{CourseView.status(course)}</dd>
        </dl>
      </section>

      <section className="detail-section">
        <h3 className="detail-section__h">Chapters</h3>
        <div className="empty-block">
          <b>Not connected yet.</b> Per-chapter progress + enrollment land when
          the lab engagement source is wired into admin.
        </div>
      </section>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Install modal
////////////////////////////////////////////////////////////////////////////////

/** Install-course modal — replaces the prod `window.prompt` chain. */
function InstallModal(props: {
  pending: boolean
  onClose: () => void
  onSubmit: (args: {
    courseId: string
    version: string
    sourcePath: string
  }) => void
}): JSX.Element {
  const [courseId, setCourseId] = useState('')
  const [version, setVersion] = useState('0.1.0')
  const [sourcePath, setSourcePath] = useState('')
  const valid = courseId.trim() !== '' && version.trim() !== ''

  return (
    <div className="lc-modal-backdrop" onClick={props.onClose}>
      <div className="lc-modal" onClick={e => e.stopPropagation()}>
        <div className="lc-modal__head">
          <div>
            <h2 className="lc-modal__title">Install course</h2>
            <div className="lc-modal__sub">
              writes a row into installed_courses
            </div>
          </div>
          <button
            className="lc-modal__close"
            onClick={props.onClose}
            aria-label="Close">
            ✕
          </button>
        </div>
        <div className="lc-modal__body">
          <div className="field">
            <label className="field__label" htmlFor="course-id">
              Course id
            </label>
            <input
              id="course-id"
              className="lc-input"
              value={courseId}
              autoFocus
              onChange={e => setCourseId(e.target.value)}
              placeholder="nix"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="course-version">
              Version
            </label>
            <input
              id="course-version"
              className="lc-input"
              value={version}
              onChange={e => setVersion(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="course-source">
              Source path
            </label>
            <input
              id="course-source"
              className="lc-input"
              value={sourcePath}
              onChange={e => setSourcePath(e.target.value)}
              placeholder="defaults to the course id"
            />
            <span className="field__hint">relative to LC_LAB_COURSES_DIR</span>
          </div>
        </div>
        <div className="lc-modal__foot">
          <button className="lc-btn lc-btn--ghost" onClick={props.onClose}>
            Cancel
          </button>
          <button
            className="lc-btn lc-btn--primary"
            disabled={!valid || props.pending}
            onClick={() =>
              props.onSubmit({
                courseId: courseId.trim(),
                version: version.trim(),
                sourcePath: sourcePath.trim() || courseId.trim(),
              })
            }>
            {props.pending ? 'Installing…' : 'Install'}
          </button>
        </div>
      </div>
    </div>
  )
}
